var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { ref, createRef } from 'lit/directives/ref.js';
import { property } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
// Components
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
import { Collapsible } from '@frontend_zz/collapsible/Collapsible.js';
// Styling
import styles from './Popover.css.js';
export const widthStrategyOptions = ['auto', 'match-trigger'];
export const transitionOptions = ['toggle', 'fade', 'collapse'];
/**
 * @element zz-popover
 */
export class Popover extends BaseElement {
    constructor() {
        super(...arguments);
        this.isVisible = false;
        this.enableArrow = false;
        this.widthStrategy = 'auto';
        this.transition = 'fade';
        this.theme = 'blue';
        this.triggerRef = createRef();
        this.popoverRef = createRef();
        this.onDocumentScroll = (_event) => {
            const { setPosition } = this;
            setPosition();
        };
        this.setPosition = () => {
            const { widthStrategy, triggerRef: { value: trigger }, popoverRef: { value: popover }, } = this;
            if (!(trigger && popover))
                return;
            // Defaults for proper layout & measuring
            popover.style.setProperty('left', '0');
            this.style.setProperty('--popover-width', 'auto');
            this.style.setProperty('--popover-height', 'auto');
            this.style.setProperty('--popover-top', '-200vh');
            this.style.setProperty('--popover-left', '-200vw');
            const triggerBounds = trigger.assignedElements()[0].getBoundingClientRect();
            const popoverBounds = popover.getBoundingClientRect();
            const safeArea = 32; // Magic number! Can't read from CSS, '1rem' and such :/
            const blockStartOrEnd = (() => {
                const doesPopoverFitBelow = popoverBounds.height < window.innerHeight - triggerBounds.bottom - safeArea;
                const whichSideHasMoreSpace = triggerBounds.top + triggerBounds.height / 2 < window.innerHeight / 2 ? 'end' : 'start';
                return doesPopoverFitBelow ? 'end' : whichSideHasMoreSpace;
            })();
            const width = (widthStrategy === 'match-trigger' ? triggerBounds : popoverBounds).width;
            popover.style.removeProperty('left');
            this.style.setProperty('--popover-trigger-height', `${triggerBounds.height}px`);
            this.style.setProperty('--popover-width', `${width}px`);
            this.style.setProperty('--popover-height', `${popoverBounds.height}px`);
            this.style.setProperty('--popover-top', `${triggerBounds.top}px`);
            this.style.setProperty('--popover-left', `${triggerBounds.left + triggerBounds.width / 2 - width / 2}px`);
            this.setAttribute('position', blockStartOrEnd);
        };
        this.renderContent = () => {
            return html `<slot></slot>`;
        };
        this.renderCollapsible = () => {
            const { isVisible, renderContent } = this;
            return html `<zz-collapsible ?is-open=${isVisible}>${renderContent()}</zz-collapsible>`;
        };
        this.renderPopover = () => {
            const { isVisible, transition, renderCollapsible, renderContent, popoverRef } = this;
            return html `
            <div ${ref(popoverRef)} class="popover transition-container" aria-hidden=${!isVisible}>
                ${when(transition === 'collapse', renderCollapsible, renderContent)}
            </div>
        `;
        };
        this.renderArrow = () => {
            const { renderPopover } = this;
            return html `
            <div class="arrow-container transition-container" role="presentation">
                <div class="arrow"></div>
            </div>
            ${renderPopover()}
            <div class="arrow-overlay transition-container" role="presentation"></div>
        `;
        };
    }
    static get styles() {
        return [styles];
    }
    static get scopedElements() {
        return {
            'zz-collapsible': Collapsible,
        };
    }
    firstUpdated() {
        const { setPosition } = this;
        setTimeout(setPosition, 0);
    }
    updated(changed) {
        const { isVisible, onDocumentScroll, setPosition } = this;
        setPosition();
        if (changed.has('isVisible')) {
            document[isVisible ? 'addEventListener' : 'removeEventListener']('scroll', onDocumentScroll);
        }
    }
    render() {
        const { enableArrow, renderArrow, renderPopover, triggerRef } = this;
        return html `
            <slot name="trigger" ${ref(triggerRef)}></slot>
            ${when(enableArrow, renderArrow, renderPopover)}
        `;
    }
}
__decorate([
    property({ type: Boolean, attribute: 'is-visible', reflect: true })
], Popover.prototype, "isVisible", void 0);
__decorate([
    property({ type: Boolean, attribute: 'enable-arrow', reflect: true })
], Popover.prototype, "enableArrow", void 0);
__decorate([
    property({ attribute: 'width-strategy', reflect: true })
], Popover.prototype, "widthStrategy", void 0);
__decorate([
    property({ reflect: true })
], Popover.prototype, "transition", void 0);
__decorate([
    property({ reflect: true })
], Popover.prototype, "theme", void 0);
